import React, { useState } from 'react';
import {
  PhotoIcon,
  VideoCameraIcon,
  DocumentIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { SearchResult } from '../hooks/useSearch';
import { isHardArchiveStorageClass, isSoftArchiveStorageClass } from '../constants/storageClasses';

// ---------------------------------------------------------------------------
// Props interface
// ---------------------------------------------------------------------------

interface SearchPanelProps {
  results: SearchResult[];
  query: string;
  isLoading: boolean;
  isCapped: boolean;
  isTimedOut: boolean;
  error: string | null;
  canDownload: boolean;
  onNavigate: (key: string) => void;
  onDownload: (key: string) => void;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Format a byte count into a human-readable string.
 * < 1 024 B → "X B"
 * < 1 048 576 B → "X.X KB"
 * < 1 073 741 824 B → "X.X MB"
 * else → "X.X GB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1_073_741_824) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
}

/**
 * Format an ISO-8601 date string to "DD Mon YYYY" (e.g. "18 Aug 2026").
 */
export function formatDate(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Return the appropriate Heroicon component for a given filename based on
 * its extension.
 */
export function getFileIcon(
  name: string
): React.ComponentType<React.SVGProps<SVGSVGElement>> {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (/^(jpg|jpeg|png|gif|webp|svg)$/.test(ext)) return PhotoIcon;
  if (/^(mp4|mov|avi|mkv|webm)$/.test(ext)) return VideoCameraIcon;
  return DocumentIcon;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SearchPanel: React.FC<SearchPanelProps> = ({
  results,
  query,
  isLoading,
  isCapped,
  isTimedOut,
  error,
  canDownload,
  onNavigate,
  onDownload,
}) => {
  // Per-row download loading state — tracks the key of the row being downloaded
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const handleDownload = async (key: string) => {
    setDownloadingKey(key);
    try {
      await onDownload(key);
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleRowKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    key: string
  ) => {
    if (e.keyCode === 13 || e.keyCode === 32) {
      e.preventDefault();
      onNavigate(key);
    }
  };

  // ------------------------------------------------------------------
  // 1. Loading state
  // ------------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
          role="status"
          aria-label="Searching"
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* 2. Capped banner */}
      {isCapped && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg p-3 text-sm">
          Showing first 200 results — refine your search for more specific results.
        </div>
      )}

      {/* 3. Timed-out banner */}
      {isTimedOut && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg p-3 text-sm">
          Search timed out — results may be incomplete. Try a more specific query or search within a subfolder.
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      {/* 4. Empty state */}
      {results.length === 0 && (
        <div className="flex flex-col items-center py-12 text-gray-500">
          <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mb-4" aria-hidden="true" />
          <p className="text-lg font-medium text-gray-900">No files found</p>
          <p className="text-sm text-gray-500 mt-1">
            No files matching &ldquo;{query}&rdquo; were found in this bucket.
          </p>
        </div>
      )}

      {/* 5. Result count label + 6. Result rows */}
      {results.length > 0 && (
        <>
          <p className="text-sm text-gray-500">
            {results.length} file{results.length !== 1 ? 's' : ''} found
          </p>

          <div className="divide-y divide-gray-100">
            {results.map((result) => {
              const Icon = getFileIcon(result.name);
              const iconColorClass = result.isImage
                ? 'text-blue-400'
                : result.isVideo
                ? 'text-purple-400'
                : 'text-gray-400';
              const isDownloading = downloadingKey === result.key;
              const isHardArchive = isHardArchiveStorageClass(result.storageClass);
              const isSoftArchive = isSoftArchiveStorageClass(result.storageClass);

              return (
                <div
                  key={result.key}
                  tabIndex={0}
                  role="button"
                  className="flex items-center justify-between py-3 px-2 rounded-md cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  onClick={() => onNavigate(result.key)}
                  onKeyDown={(e) => handleRowKeyDown(e, result.key)}
                >
                  {/* Left: icon + name + key path + optional archive badge */}
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <Icon
                      className={`h-8 w-8 flex-shrink-0 ${iconColorClass}`}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate flex items-center">
                        {result.name}
                        {isHardArchive && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 ml-2">
                            Archived
                          </span>
                        )}
                        {isSoftArchive && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 ml-2">
                            Instant Archive
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{result.key}</p>
                    </div>
                  </div>

                  {/* Right: size + date + optional download */}
                  <div className="flex items-center space-x-3 flex-shrink-0 ml-4">
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{formatFileSize(result.size)}</p>
                      <p className="text-xs text-gray-500">{formatDate(result.lastModified)}</p>
                    </div>

                    {canDownload && (
                      <button
                        type="button"
                        aria-label={
                          isHardArchive
                            ? 'Cannot download: file is in Glacier archive storage'
                            : `Download ${result.name}`
                        }
                        disabled={isDownloading || isHardArchive}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isHardArchive) void handleDownload(result.key);
                        }}
                        className={`p-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                          isHardArchive
                            ? 'text-gray-300 opacity-50 cursor-not-allowed'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                      >
                        {isDownloading ? (
                          <div
                            className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"
                            role="status"
                            aria-label="Downloading"
                          />
                        ) : (
                          <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default SearchPanel;
